<template>
  <nav class="app-menu-bar flex h-16 w-full items-center justify-between rounded-lg border px-6">
    <!-- Left Side: Button Group -->
    <div class="flex items-center space-x-4">
      <template v-for="btn in buttons">
        <component
          v-if="display_button(btn)"
          :is="btn.href ? RouterLink : 'button'"
          :to="btn.href"
          :id="btn.id"
          @click="handleButtonClick(btn)"
          :class="[
            // If a title exists, use extra horizontal padding for the text.
            btn.title
              ? 'small-button flex items-center rounded border px-4 py-2 shadow'
              : 'small-button flex h-10 w-10 items-center justify-center rounded border shadow',
            // Active state styling.
            btn.href && isActiveRoute(btn.href) ? 'bg-primary text-white' : 'hover:bg-primary'
          ]"
        >
          <a-tooltip :title="btn.toolTip" placement="bottom">
            <component
              :is="btn.animate && btn.animationIcon ? btn.animationIcon : btn.icon"
              :dummy="btn.animate"
              class="h-6 w-6 select-none focus:outline-none"
              :style="{ userSelect: 'none' }"
              :class="{ 'animate-spin': btn.animate }"
              draggable="false"
              unselectable="on"
            />
          </a-tooltip>
          <!-- Conditionally render the title text -->
          <span v-if="btn.title" class="ml-2">{{ btn.title }}</span>
        </component>
      </template>
      <!-- Date Range Selector (only if showDateRange is true) -->
      <DateRangeSelector v-if="showDateRange" @dateRangeChanged="handleDateRangeChanged" />
      <!-- Additional filters slot -->
      <slot name="filters" />
    </div>

    <!-- Right Side: Search Input (only rendered if showSearch is true) -->
    <div class="flex items-center" v-if="showSearch">
      <div class="flex h-full items-center">
        <a-input-search
          placeholder="Search local"
          :value="searchValue"
          @input="handleSearch"
          class="search-input-aligned"
        />
      </div>
    </div>
  </nav>
  <div class="mb-2"></div>
  <div class="w-full">
    <div class="flex flex-col items-end text-xs text-muted-foreground">
      <div v-for="message in messages" class="mb-1">
        {{ message }}
      </div>
    </div>
  </div>
  <Separator class="my-6" />
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { Separator } from '@/components/ui/separator'
  import { RouterLink, useRoute } from 'vue-router'
  import { ApplicationConfiguration } from '@/types'
  import configService from '@/services/configService'
  import DateRangeSelector from '@/components/DateRangeSelector.vue'

  const appConfig = ref<ApplicationConfiguration | null>(null)

  // Define a TypeScript interface for the button objects
  interface Button {
    icon: any // Replace 'any' with a more specific type if you have one (e.g., Component)
    onClick?: () => void
    animate?: boolean // animate the button
    animationIcon?: any // a different icon to use during animation
    title?: string
    toolTip?: string
    href?: string
    environments?: string[]
    id?: string
  }

  // Define props for the component
  const props = defineProps<{
    showSearch?: boolean
    showDateRange?: boolean
    onSearch?: (event: Event) => void
    onDateRangeChanged?: () => void
    buttons?: Button[]
    messages?: string[]
    searchValue?: string
  }>()

  // Provide default values if props are not passed in
  const showSearch = props.showSearch ?? false
  const showDateRange = props.showDateRange ?? false
  const buttons = props.buttons ?? []
  const onSearch = props.onSearch ?? ((_event: Event) => {})
  const onDateRangeChanged = props.onDateRangeChanged ?? (() => {})
  const route = useRoute()

  // Handle search input changes
  function handleSearch(event: Event) {
    onSearch(event)
  }

  // Handle date range changes
  function handleDateRangeChanged() {
    onDateRangeChanged()
  }

  async function handleButtonClick(btn: Button) {
    if (btn.onClick) {
      btn.onClick()
    }
  }

  // This function returns true if the button's route matches the current route.
  function isActiveRoute(path: string): boolean {
    return route.path === path
  }

  function display_button(btn: Button): boolean {
    if (btn.environments && btn.environments.length > 0 && appConfig?.value?.environment?.name) {
      return btn.environments.includes(appConfig?.value?.environment?.name)
    }
    return true
  }

  onMounted(async () => {
    await configService.loadApplicationConfig()
    appConfig.value = configService.getApplicationConfigData()
  })
</script>

<style>
  .ant-btn-default {
    box-shadow: none !important;
  }

  .no-select {
    user-select: none;
  }

  .search-input-aligned .ant-input-search-button {
    display: flex !important;
    justify-content: center !important;
    align-items: center;
    height: 40px;
  }

  .ant-input-group-addon {
    background-color: var(--background) !important;
  }
</style>
